/**
 * Pagination — one control for every paged collection, list, table, or cards.
 *
 * Two sources of truth exist in this platform and the control speaks both:
 *
 *   Offset mode (`total` supplied) — the Worker filtered, sorted, and then
 *   sliced, so it knows how many records matched. Renders "1–25 of 340".
 *
 *   Cursor mode (`total` omitted) — the source can only hand out the next page,
 *   never a count. Renders "Page 2" and relies on `hasNext`. Calls use this:
 *   their store is cursor-paged and shared with another portal, so asking it for
 *   a total would mean changing a service two products depend on.
 *
 * Both modes render identically apart from the readout, so a user moving
 * between Leads and Calls does not meet a second pagination idiom.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

export interface PaginationProps {
  /** 1-based. */
  page: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  /** How many rows the current page actually rendered. */
  count: number;
  /** Total matching records. Omit for cursor-paged sources that cannot count. */
  total?: number;
  /** Cursor mode: whether the source offered another page. */
  hasNext?: boolean;
  /** Plural noun for the readout, e.g. "leads". */
  label?: string;
  /** Disable both controls while a page request is in flight. */
  busy?: boolean;
}

export function Pagination({
  page,
  onPageChange,
  pageSize,
  count,
  total,
  hasNext,
  label = 'records',
  busy = false,
}: PaginationProps) {
  const knowsTotal = typeof total === 'number';
  const nextAvailable = knowsTotal ? page * pageSize < total : Boolean(hasNext);

  // A single page of results needs no controls. Checked before any early
  // return so an empty or short list simply renders nothing.
  if (page === 1 && !nextAvailable) return null;

  const first = (page - 1) * pageSize + 1;
  const last = first + Math.max(0, count - 1);

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-3"
      aria-label={`${label} pagination`}
    >
      <p className="text-[12px] text-muted-foreground" aria-live="polite">
        {knowsTotal ? (
          count === 0 ? (
            <>No {label} on this page</>
          ) : (
            <>
              <span className="font-medium text-foreground tabular-nums">
                {first}–{last}
              </span>{' '}
              of <span className="tabular-nums">{total}</span> {label}
            </>
          )
        ) : (
          <>
            Page <span className="font-medium text-foreground tabular-nums">{page}</span>
            {count > 0 && (
              <>
                {' · '}
                <span className="tabular-nums">{count}</span> {label}
              </>
            )}
          </>
        )}
      </p>
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1"
          disabled={busy || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          Previous
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1"
          disabled={busy || !nextAvailable}
          onClick={() => onPageChange(page + 1)}
        >
          Next
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </nav>
  );
}

export const DEFAULT_PAGE_SIZE = 25;
